import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { MapPin, Filter, Calendar as CalIcon } from "lucide-react";
import { useCampusLocations } from "@/hooks/useCampusLocations";
import { useUpcomingCourses } from "@/hooks/useUpcomingCourses";

export default function CalendarPage() {
  const [loc, setLoc] = useState("all");
  const { locations, loading: locationsLoading } = useCampusLocations();
  const { courses, loading: coursesLoading, error: coursesError } = useUpcomingCourses(loc);

  const locationOptions = useMemo(() => {
    return (locations || [])
      .filter((campus) => !campus.isDeleted && !campus.isDisable)
      .map((campus) => ({
        id: campus.campusId,
        name: campus.campusName || "Unnamed location",
      }));
  }, [locations]);

  const formatTime = (value) => {
    if (!value) return "";
    const [hours = "0", minutes = "0"] = String(value).split(":");
    const date = new Date();
    date.setHours(Number(hours), Number(minutes), 0, 0);
    return date.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });
  };

  const getScheduleDate = (c) => {
    const rawDate = c.ScheduleDate || c.date || c.scheduleDate;
    if (!rawDate) return null;
    const parsed = new Date(rawDate);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const getScheduleTime = (c) => {
    const start = c.StartTime || c.startTime;
    const end = c.EndTime || c.endTime;
    if (start && end) return `${formatTime(start)} - ${formatTime(end)}`;
    return c.time || "";
  };

  const getLocationText = (c) => {
    const campusName = c.CampusName || c.location || c.campusName;
    const suburb = c.Suburb || c.suburb;
    const postcode = c.PostCode || c.postCode;
    return [campusName, suburb, postcode].filter(Boolean).join(", ");
  };

  const getCourseTitle = (c) => {
    const courseName = c.CourseName || c.courseName || "White Card";
    const courseCode = c.CourseCode || c.courseCode || "CPCWHS1001";
    return `${courseName} · ${courseCode}`;
  };

  const getAvailableSeats = (c) => Number(c.AvailableSeats ?? c.availableSeats ?? c.seats ?? 0);
  const getCapacity = (c) => Number(c.MaxSeats ?? c.maxSeats ?? c.capacity ?? 0);
  const getLocationId = (c) => c.CampusId ?? c.campusId ?? c.locationId ?? c.CampusID;
  const getScheduleId = (c) => c.CourseScheduleId ?? c.id ?? c.courseScheduleId;
  const getPrice = (c) => c.CourseFee ?? c.price ?? 135;

  const filtered = useMemo(() => {
    if (loc === "all") return courses;

    return courses.filter((c) => String(getLocationId(c)) === String(loc));
  }, [loc, courses]);

  return (
    <div>
      <section className="bg-[#0A2540] text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-sm font-semibold tracking-wide uppercase text-orange-400 mb-3">Course calendar</div>
          <h1 className="text-4xl lg:text-5xl font-display font-extrabold">Upcoming White Card classes</h1>
          <p className="mt-3 text-lg text-slate-300 max-w-2xl">Choose a date, location and time that suits you. All classes run 8:30 AM - 3:30 PM.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="calendar-filters">
          <div className="flex items-center gap-2 text-slate-500 text-sm"><Filter className="w-4 h-4" /> Filter by location:</div>
          <button onClick={() => setLoc("all")} data-testid="filter-all" className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${loc === "all" ? "bg-[#0A2540] text-white border-[#0A2540]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>All locations</button>
          {locationsLoading ? (
            <span className="text-sm text-slate-500">Loading locations...</span>
          ) : locationOptions.length === 0 ? (
            <span className="text-sm text-slate-500">No locations available</span>
          ) : (
            locationOptions.map((l) => (
              <button key={l.id} onClick={() => setLoc(l.id)} data-testid={`filter-${l.id}`} className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${loc === l.id ? "bg-[#0A2540] text-white border-[#0A2540]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>{l.name}</button>
            ))
          )}
        </div>

        {coursesError ? (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">{coursesError}</div>
        ) : null}

        {coursesLoading && courses.length === 0 ? (
          <div className="text-sm text-slate-500">Loading upcoming classes...</div>
        ) : null}

        {!coursesLoading && filtered.length === 0 ? (
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">No upcoming classes are available right now.</div>
        ) : null}

        <div className="grid gap-3" data-testid="calendar-list">
          {filtered.map((c) => {
            const scheduleDate = getScheduleDate(c);
            const availableSeats = getAvailableSeats(c);
            const capacity = getCapacity(c);
            const full = availableSeats <= 0;
            const low = availableSeats > 0 && availableSeats < 5;
            const scheduleId = getScheduleId(c);
            const courseTitle = getCourseTitle(c);
            const locationText = getLocationText(c);
            const timeText = getScheduleTime(c);
            const price = getPrice(c);

            return (
              <div key={scheduleId} className="wc-card flex flex-col md:flex-row md:items-center gap-4">
                <div className="md:w-32 flex md:flex-col items-center md:items-start gap-3">
                  <div className="w-16 h-16 rounded-lg bg-[#0A2540] text-white flex flex-col items-center justify-center">
                    <div className="text-xs uppercase font-semibold text-orange-300">{scheduleDate ? scheduleDate.toLocaleDateString("en-AU", { month: "short" }) : ""}</div>
                    <div className="text-2xl font-display font-extrabold leading-none">{scheduleDate ? scheduleDate.getDate() : ""}</div>
                  </div>
                  <div className="md:mt-1 text-xs font-semibold text-slate-500 uppercase">{c.day || (scheduleDate ? scheduleDate.toLocaleDateString("en-AU", { weekday: "short" }) : "")}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-extrabold text-lg text-[#0A2540]">{courseTitle}</div>
                  <div className="text-sm text-slate-600 mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span className="flex items-center gap-1"><CalIcon className="w-3.5 h-3.5" />{timeText}</span>
                    <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{locationText}</span>
                    <span>1 day · 6 hours</span>
                  </div>
                </div>
                <div className="flex items-center gap-4 md:justify-end">
                  <div className="text-right">
                    <div className={`text-sm font-bold ${full ? "text-rose-600" : low ? "text-amber-600" : "text-emerald-600"}`}>{full ? "Sold out" : availableSeats > 10 ? "Available" : `${availableSeats} ${availableSeats === 1 ? "seat" : "seats"} left`}</div>
                    <div className="text-xs text-slate-500">${price} inc GST</div>
                  </div>
                  <Link
                    to={full ? "#" : `/checkout/${scheduleId}`}
                    state={{ selectedCourse: c }}
                    data-testid={`book-${scheduleId}`}
                    className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${full ? "bg-slate-100 text-slate-400 pointer-events-none" : "bg-orange-500 text-white hover:bg-orange-600"}`}
                  >
                    {full ? "Full" : "Book"}
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
